import {useState} from 'react'
import {GET} from '../../../../services/api'
import {
  IFindPaginatedResponse,
  IUsageLog,
  IPageMeta,
  ITopRouteItem,
  ITopPluginspaceItem,
  ITopDataUsageItem,
  IGeneralStats,
  IAwsCostEstimate,
  IPluginspaceStatsItem,
  TableParams,
  ReportFilters,
  ITopRoutesResponse,
  ITopPluginspacesResponse,
  IGeneralStatsResponse,
  ITopDataUsageResponse,
  IPluginspaceStatsResponse,
} from '../types'

// Preços AWS (us-east-1) - referência
const AWS_API_GATEWAY_PER_MILLION = 3.5 // USD por 1M de requests (REST API)
const AWS_DATA_TRANSFER_OUT_PER_GB = 1.0 // USD por GB (primeiros 10TB)
const AWS_DATA_TRANSFER_IN_PER_GB =  1.0  // Entrada é gratuita


export function useUsageCounter() {
  const [logs, setLogs] = useState<IUsageLog[]>([])
  const [meta, setMeta] = useState<IPageMeta | null>(null)
  const [topRoutes, setTopRoutes] = useState<ITopRouteItem[]>([])
  const [topPluginspaces, setTopPluginspaces] = useState<ITopPluginspaceItem[]>([])
  const [topDataUsage, setTopDataUsage] = useState<ITopDataUsageItem[]>([])
  const [generalStats, setGeneralStats] = useState<IGeneralStats | null>(null)
  const [dollarRate, setDollarRate] = useState<number | null>(null)
  const [costEstimate, setCostEstimate] = useState<IAwsCostEstimate | null>(null)
  const [pluginspaceStats, setPluginspaceStats] = useState<IPluginspaceStatsItem[]>([])
  const [loading, setLoading] = useState(false)

  // Busca cotação do dólar (BCB - Banco Central do Brasil)
  const fetchDollarRate = async (): Promise<number | null> => {
    try {
      const res = await fetch(
        'https://economia.awesomeapi.com.br/json/last/USD-BRL'
      )
      const data = await res.json()
      const rate = parseFloat(data.USDBRL.bid)
      setDollarRate(rate)
      return rate
    } catch (e) {
      console.error('Erro ao buscar cotação do dólar:', e)
      return null
    }
  }

  // Calcula estimativa de custo AWS com base nos general stats
  const calculateCost = (stats: IGeneralStats, rate: number): IAwsCostEstimate => {
    const totalRequests = parseInt(stats.totalRequests)
    const totalResponseBytes = parseInt(stats.totalResponseSize)
    const totalRequestBytes = parseInt(stats.totalRequestSize)

    const apiGatewayCostUsd = (totalRequests / 1_000_000) * AWS_API_GATEWAY_PER_MILLION
    const dataTransferOutCostUsd = (totalResponseBytes / (1024 * 1024 * 1024)) * AWS_DATA_TRANSFER_OUT_PER_GB
    const dataTransferInCostUsd = (totalRequestBytes / (1024 * 1024 * 1024)) * AWS_DATA_TRANSFER_IN_PER_GB
    const totalCostUsd = apiGatewayCostUsd + dataTransferOutCostUsd + dataTransferInCostUsd
    const totalCostBrl = totalCostUsd * rate

    return {
      apiGatewayCostUsd,
      dataTransferOutCostUsd,
      dataTransferInCostUsd,
      totalCostUsd,
      totalCostBrl,
      dollarRate: rate,
    }
  }

  // GET /usage-log
  const getLogs = async (params?: TableParams) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()

      if (params?.page !== undefined) query.append('page', String(params.page))
      if (params?.take !== undefined) query.append('take', String(params.take))
      if (params?.method) query.append('method', params.method)
      if (params?.route) query.append('route', params.route)
      if (params?.createdFrom) query.append('createdFrom', params.createdFrom+'T00:00:00')
      if (params?.createdTo) query.append('createdTo', params.createdTo+'T23:59:59')

      const queryString = query.toString()
      const res: IFindPaginatedResponse = await GET(`/usage-log${queryString ? `?${queryString}` : ''}`)

      setLogs(res.data)
      if (res.meta) setMeta(res.meta)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/:id
  const getLogById = async (id: number) => {
    try {
      setLoading(true)
      const res = await GET(`/usage-log/${id}`)
      return res.data as IUsageLog
    } catch (e) {
      console.error(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/report/top-routes
  const getTopRoutes = async (limit = 10, days = 30, filters?: ReportFilters) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()
      query.append('limit', String(limit))
      
      if (filters?.method) query.append('method', filters.method)
      if (filters?.route) query.append('route', filters.route)
      if (filters?.createdFrom) query.append('createdFrom', filters.createdFrom+'T00:00:00')
      if (filters?.createdTo) query.append('createdTo', filters.createdTo+'T23:59:59')
      const res :ITopRoutesResponse = await GET(`/usage-log/report/top-routes?${query.toString()}`)
      setTopRoutes( res.success ? res.data : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/report/top-pluginspaces
  const getTopPluginspaces = async (limit = 10, days = 30, filters?: ReportFilters) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()
      query.append('limit', String(limit))
      
      if (filters?.method) query.append('method', filters.method)
      if (filters?.route) query.append('route', filters.route)
      if (filters?.createdFrom) query.append('createdFrom', filters.createdFrom+'T00:00:00')
      if (filters?.createdTo) query.append('createdTo', filters.createdTo+'T23:59:59')
      const res :ITopPluginspacesResponse = await GET(`/usage-log/report/top-pluginspaces?${query.toString()}`)
      setTopPluginspaces( res.success ? res.data : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/report/top-data-usage
  const getTopDataUsage = async (days = 7, limit = 10, filters?: ReportFilters) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()
      
      query.append('limit', String(limit))
      if (filters?.method) query.append('method', filters.method)
      if (filters?.route) query.append('route', filters.route)
      if (filters?.createdFrom) query.append('createdFrom', filters.createdFrom+'T00:00:00')
      if (filters?.createdTo) query.append('createdTo', filters.createdTo+'T23:59:59')
      const res :ITopDataUsageResponse = await GET(`/usage-log/report/top-data-usage?${query.toString()}`)
      setTopDataUsage( res.success ? res.data : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/report/general-stats
  const getGeneralStats = async (days = 30, filters?: ReportFilters) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()
      if (filters?.method) query.append('method', filters.method)
      if (filters?.route) query.append('route', filters.route)
      if (filters?.createdFrom) query.append('createdFrom', filters.createdFrom+'T00:00:00')
      if (filters?.createdTo) query.append('createdTo', filters.createdTo+'T23:59:59')
      const res :IGeneralStatsResponse = await GET(`/usage-log/report/general-stats?${query.toString()}`)
      const stats = res.success ? res.data : null
      setGeneralStats(stats)

      // Calcula custos se tiver stats e cotação
      if (stats) {
        let rate = dollarRate
        if (!rate) {
          rate = await fetchDollarRate()
        }
        if (rate) {
          setCostEstimate(calculateCost(stats, rate))
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  // GET /usage-log/report/pluginspace-stats
  const getPluginspaceStats = async (limit = 10, filters?: ReportFilters) => {
    try {
      setLoading(true)
      const query = new URLSearchParams()
      query.append('limit', String(limit))
      if (filters?.method) query.append('method', filters.method)
      if (filters?.route) query.append('route', filters.route)
      if (filters?.createdFrom) query.append('createdFrom', filters.createdFrom+'T00:00:00')
      if (filters?.createdTo) query.append('createdTo', filters.createdTo+'T23:59:59')
      const res: IPluginspaceStatsResponse = await GET(`/usage-log/report/pluginspace-stats?${query.toString()}`)
      setPluginspaceStats(res.success ? res.data : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const init = async () => {
    setLoading(true)
    await Promise.all([getLogs(), getTopRoutes(), getTopPluginspaces(), getTopDataUsage(), getGeneralStats()])
    setLoading(false)
  }

  return {
    logs,
    meta,
    topRoutes,
    topPluginspaces,
    topDataUsage,
    generalStats,
    costEstimate,
    dollarRate,
    pluginspaceStats,
    loading,
    getLogs,
    getLogById,
    getTopRoutes,
    getTopPluginspaces,
    getTopDataUsage,
    getGeneralStats,
    getPluginspaceStats,
    fetchDollarRate,
    init,
  }
}